import React, { useMemo } from 'react';

import moment from 'moment';

import AGChart from 'components/shared/AGChart/AGChart';
import WidgetHeader from 'components/shared/CustomizableBoard/widgets/components/WidgetHeader';

import {
  camelCaseToReadableFormat,
  formatLargeNumber,
  mString
} from '../../../../../lib/helpers/utility';

const sets = {
  accountReceivable: '#6D828B',
  insurancePayments: '#2D9CDB',
  adjustments: '#EF4444',
  patientPayments: '#13B9FF',
  technologyFee: '#8191E8',
  totalReceived: '#22C55E'
};

export default function GrandTotal(props) {
  const overview = props?.overview || {};
  const totals = overview?.totals;

  // Convert graphData into ag-charts friendly format
  const chartData = useMemo(() => {
    const graphData = overview?.graphData || {};
    return Object.keys(graphData).map((date) => ({
      date: moment.unix(date).format('MMM D'),
      ...Object.fromEntries(
        Object.entries(graphData[date]).map(([key, val]) => [key, parseFloat(val) || 0])
      )
    }));
  }, [overview]);

  // Chart options for ag-charts
  const options = useMemo(() => {
    return {
      data: chartData,
      series: Object.keys(sets).map((set) => {
        return {
          type: 'line',
          xKey: 'date',
          yKey: set,
          yName: camelCaseToReadableFormat(set),
          stroke: sets[set],
          marker: {
            fill: sets[set],
            stroke: sets[set]
          }
        };
      }),
      legend: {
        position: 'top'
      },
      axes: [
        {
          type: 'category',
          position: 'bottom',
          gridLine: {
            style: [{ stroke: '#CDD5D8' }]
          }
        },
        {
          type: 'number',
          position: 'left',
          label: {
            formatter: ({ value }) => mString(value) // adds commas
          }
        }
      ],
      theme: {
        overrides: {
          line: {
            series: {
              tooltip: {
                enabled: true,
                renderer: ({ datum, xKey }) => {
                  const rows = Object.keys(sets)
                    .map((key) => {
                      return `<div style="display:flex; justify-content:space-between; gap:10px; margin-bottom:2px;">
                                <span>${camelCaseToReadableFormat(key)}</span>
                                <span>${mString(datum[key])}</span>
                              </div>`;
                    })
                    .join('');

                  return {
                    title: datum[xKey],
                    content: `<div>${rows}</div>` // HTML content
                  };
                }
              }
            }
          }
        }
      }
    };
  }, [chartData]);

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <WidgetHeader hasPlus={false} icon="chart-mono" iconColor="primary" {...props} />
      <div className="grid grid-cols-6 justify-between gap-3 !p-5 !px-7 md:grid-cols-3">
        {Object.keys(totals || {})?.map((total, index) => (
          <div className="flex flex-col" key={index}>
            <p className="text-lg font-medium text-primary-900">
              {totals[total] < 0 ? '-' : ''}${formatLargeNumber(Math.abs(totals[total]).toFixed(2))}
            </p>
            <p className="text-[13px] text-primary-800">{camelCaseToReadableFormat(total)}</p>
          </div>
        ))}
      </div>
      <div className="max-h-[60%] w-full flex-1 !p-4 !pt-0">
        <AGChart options={options} />
      </div>
    </div>
  );
}
